import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common/Bits";
import { fmtDate } from "@/lib/constants";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

export default function ValueMonitoring() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/metrics").then(({ data }) => setRows(data)); }, []);

  if (!rows) return <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-44 rounded-xl" />)}</div>;

  const grouped = rows.reduce((acc, m) => {
    (acc[m.initiative_name] = acc[m.initiative_name] || []).push(m);
    return acc;
  }, {});

  return (
    <div>
      <PageHeader title="Monitoreo de valor" subtitle="Resultados reales del portafolio comparados con el valor esperado y las metas comprometidas." testId="value-header" />
      {rows.length === 0 && <EmptyState icon={TrendingUp} title="Sin métricas registradas" />}
      <div className="space-y-8">
        {Object.entries(grouped).map(([name, metrics]) => (
          <div key={name}>
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h2 className="font-display text-lg font-semibold">{name}</h2>
              <Badge variant="outline" className="bg-primary/8 text-primary border-primary/20">Valor esperado: {metrics[0].expected_value}</Badge>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {metrics.map((m) => {
                const trend = m.previous == null ? null : m.value > m.previous ? "up" : m.value < m.previous ? "down" : "flat";
                const pct = m.target ? Math.min(100, Math.round((m.value / m.target) * 100)) : null;
                return (
                  <Card key={m.id} className="shadow-card border-border" data-testid={`metric-card-${m.id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-medium text-muted-foreground">{m.metric} · {m.period}</p>
                        {trend === "up" && <TrendingUp className="h-4 w-4 text-success" />}
                        {trend === "down" && <TrendingDown className="h-4 w-4 text-risk-high" />}
                        {trend === "flat" && <Minus className="h-4 w-4 text-muted-foreground" />}
                      </div>
                      <p className="font-display text-3xl font-semibold mt-2 tabular">{m.value} <span className="text-sm font-normal text-muted-foreground">{m.unit}</span></p>
                      {pct != null && (
                        <div className="mt-3">
                          <div className="flex justify-between text-[11px] text-muted-foreground mb-1"><span>Meta: {m.target}</span><span>{pct}%</span></div>
                          <Progress value={pct} className="h-1.5" />
                        </div>
                      )}
                      <p className="text-[11px] text-muted-foreground mt-3">Anterior: {m.previous ?? "—"} · {m.source} · {m.owner} · {fmtDate(m.updated_at)}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground mt-8">Para registrar nuevas métricas, abra la ficha de la iniciativa en el <Link to="/registro" className="text-primary underline underline-offset-2">AI Registry</Link>.</p>
    </div>
  );
}
